<template>
  <v-dialog v-model="dialogVisible" width="700" persistent no-click-animation scrollable overlay-color="grey-500">
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar color="transparent" flat height="46">
          <v-toolbar-title class="px-5 subtitle-2 grey-500--text font-weight-semibold">{{ title }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-3">
              <v-btn @click="close" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-divider />
      <v-card-text class="py-5 px-5">
        <!-- Alert -->
        <v-alert class="inf-alert-yellow yellow-700--text rounded-md mb-5" dense outlined>
          <div class="d-flex py-2 align-center">
            <v-icon color="yellow-700" size="28">mdi-alert-outline</v-icon>
            <div class="ml-4">
              <span class="body-1 font-weight-bold yellow-700--text">Atención</span>
              <span class="d-block body-2 yellow-700--text">{{ alertMessage }}</span>
            </div>
          </div>
        </v-alert>

        <!-- Supplier info -->
        <div class="d-flex align-center mb-5">
          <v-icon color="grey-500" size="24">mdi-human-dolly</v-icon>
          <span class="ml-2 body-1 font-weight-bold grey-500--text">{{ supplier.name || 'Sin nombre' }}<span v-if="supplier.tax_id">, RUT {{ supplier.tax_id }}</span></span>
        </div>

        <!-- Categories row -->
        <div class="d-flex align-end">
          <!-- Current category -->
          <div style="flex: 1; min-width: 0;">
            <div class="d-flex align-center mb-2">
              <v-icon color="grey-500" size="18">mdi-folder</v-icon>
              <span class="ml-2 body-2 grey-500--text">Categoría actual</span>
            </div>
            <v-text-field
              :value="currentCategoryName"
              outlined
              single-line
              dense
              hide-details
              readonly
              disabled
            />
          </div>
          <!-- Arrow -->
          <div class="d-flex justify-center align-center pb-2" style="width: 60px; flex-shrink: 0;">
            <v-icon color="grey-500" size="28">mdi-arrow-right-thin</v-icon>
          </div>
          <!-- New category -->
          <div style="flex: 1; min-width: 0;">
            <div class="d-flex align-center mb-2">
              <v-icon color="grey-500" size="18">mdi-folder</v-icon>
              <span class="ml-2 body-2 grey-500--text">Nueva categoría</span>
            </div>
            <v-select
              v-model="selectedCategory"
              hide-details
              single-line
              color="blue-500"
              dense
              outlined
              :items="filteredCategoriesList"
              :chips="false"
              item-text="name"
              item-value="id"
              item-disabled="disabled"
              placeholder="Selecciona la nueva categoría"
              no-data-text="No existen categorías para mostrar"
              :menu-props="{ bottom: true, offsetY: true, maxHeight: 200 }"
            />
          </div>
        </div>

        <!-- Confirm input -->
        <div class="mt-6">
          <span class="d-block grey-500--text mb-2">Escribe <span class="font-weight-bold">"confirmar"</span> para continuar.</span>
          <v-text-field v-model="confirm" outlined single-line maxlength="9" hide-details @keyup.enter="confirm.toLowerCase() === 'confirmar' && handleConfirm()" />
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-5 py-2">
        <v-spacer/>
        <v-btn outlined @click="close">Cancelar</v-btn>
        <v-btn color="blue-500" @click="handleConfirm" :loading="loading" :disabled="confirm.toLowerCase() !== 'confirmar' || !selectedCategory">Confirmar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SupplierCategoryModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    actionType: {
      type: String,
      default: 'add', // 'add' or 'change'
      validator: (val) => ['add', 'change'].includes(val)
    },
    supplier: {
      type: Object,
      default: () => ({})
    },
    categoriesList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      defaultSegmentId: 'se_AAAAAAAAAAAAAAAA',
      selectedCategory: '',
      confirm: ''
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    title () {
      return this.actionType === 'add' ? 'Agregar a categoría' : 'Cambiar de categoría'
    },
    alertMessage () {
      if (this.actionType === 'change') {
        return `Al cambiar la categoría del proveedor ${this.supplier.name || 'Sin nombre'}, se le aplicarán las reglas de recepción correspondientes a la nueva categoría.`
      }
      return `Al agregar el proveedor ${this.supplier.name || 'Sin nombre'} a una categoría, se le aplicarán las reglas de recepción correspondientes.`
    },
    currentCategoryName () {
      return this.supplier.category_details?.name || this.supplier.group_details?.name || 'Sin categoría asociada'
    },
    filteredCategoriesList () {
      return this.categoriesList.map(category => {
        const isDefault = category.id === this.defaultSegmentId
        const isCurrentCategory = category.id === this.supplier?.segment_id
        return {
          ...category,
          name: isDefault ? 'Sin categoría' : category.name,
          disabled: isCurrentCategory
        }
      })
    }
  },
  watch: {
    value (val) {
      if (!val) {
        this.clear()
      }
    }
  },
  methods: {
    handleConfirm () {
      if (this.confirm.toLowerCase() !== 'confirmar' || !this.selectedCategory) return
      this.$emit('confirm', {
        supplier: this.supplier,
        newCategoryId: this.selectedCategory,
        oldCategoryId: this.supplier.segment_id,
        actionType: this.actionType
      })
    },
    clear () {
      this.selectedCategory = ''
      this.confirm = ''
    },
    close () {
      this.clear()
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.inf-alert-yellow.v-alert--outlined {
  border-color: var(--light-yellow-700) !important;
  background-color: var(--light-yellow-100) !important;
}
</style>
